import type { EClassRoomDisciplinesStatus } from '@/shared/api/generated';

/** Colunas ordenáveis da tabela (mapeadas para `SortField` no hook de listagem). */
export type ClassPlanningSortField = 'dateStart' | 'disciplineName' | 'status';

/**
 * Filtros aplicados na listagem de planejamento de aulas.
 *
 * Espelha os query params do endpoint `GET /api/v1/classroomdiscipline`:
 * `CompanyId`, `CourseId`, `ClassStudentId`, `ClassModuleId`, `Status`, `DateStart`,
 * `DateEnd` e `Teacher`. Os campos `*Label` guardam o rótulo exibido nos
 * autocompletes (não vão para a API). As datas usam o formato ISO `YYYY-MM-DD`
 * emitido pelo `DatePicker`. `teacherName` é enviado como texto livre (`Teacher`
 * não é um id — ver `design.md` da change `add-class-planning-listing`).
 */
export interface ClassPlanningFilters {
  /** Identificador da empresa selecionada (`CompanyId`), ou `null`. */
  readonly companyId: number | null;
  /** Rótulo da empresa selecionada (exibição no autocomplete). */
  readonly companyLabel: string;
  /** Identificador do curso selecionado (`CourseId`), ou `null`. */
  readonly courseId: number | null;
  /** Rótulo do curso selecionado (exibição no autocomplete). */
  readonly courseLabel: string;
  /** Identificador da turma selecionada (`ClassStudentId`), ou `null`. */
  readonly classStudentId: number | null;
  /** Rótulo da turma selecionada (exibição no autocomplete). */
  readonly classStudentLabel: string;
  /** Identificador do módulo selecionado (`ClassModuleId`), ou `null`. */
  readonly classModuleId: number | null;
  /** Rótulo do módulo selecionado (exibição no autocomplete). */
  readonly classModuleLabel: string;
  /** Status selecionados (multi-seleção) para o filtro `Status`. */
  readonly status: readonly EClassRoomDisciplinesStatus[];
  /** Data inicial (ISO `YYYY-MM-DD`) para o filtro `DateStart`. */
  readonly dateStart: string;
  /** Data final (ISO `YYYY-MM-DD`) para o filtro `DateEnd`. */
  readonly dateEnd: string;
  /** Nome do professor (texto livre) para o filtro `Teacher`. */
  readonly teacherName: string;
  /** Coluna usada para ordenação server-side (`SortField`), ou `null` (ordem padrão). */
  readonly sortField: ClassPlanningSortField | null;
  /** Direção da ordenação (`SortDirection`). */
  readonly sortDirection: 'asc' | 'desc';
  /** Página atual (1-indexed) para `PageNumber`. */
  readonly page: number;
}

/** Estado inicial (sem filtros, primeira página). */
export const INITIAL_CLASS_PLANNING_FILTERS: ClassPlanningFilters = {
  companyId: null,
  companyLabel: '',
  courseId: null,
  courseLabel: '',
  classStudentId: null,
  classStudentLabel: '',
  classModuleId: null,
  classModuleLabel: '',
  status: [],
  dateStart: '',
  dateEnd: '',
  teacherName: '',
  sortField: null,
  sortDirection: 'asc',
  page: 1,
};
